import styled, { keyframes, css } from 'styled-components'
import React, { useEffect, useState } from 'react'
import { CSSTransition, TransitionStatus } from 'react-transition-group'
import { useTranslation } from 'next-i18next'

import Text from '../../components/Text'
import { COLORS } from '../../utils/colorUtils'

const flipInX = keyframes`
   0% {
    -webkit-transform: perspective(400px) rotateX(90deg) translateX(-50%);
      visibility: 0;
   }
   100% {
    -webkit-transform: perspective(400px) rotateX(0deg) translateX(-50%);
      opacity: 1;
   }
`

const Background = styled.div`
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const ContentWrapper = styled.div`
  z-index: 2;
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const GlassMorphism = styled.div<{ state: TransitionStatus }>`
  position: absolute;

  @media (min-width: 768px) {
    width: ${540 * 0.7}px;
    height: ${704 * 0.7}px;
  }

  @media (min-width: 1024px) {
    width: ${540 * 0.8}px;
    height: ${704 * 0.8}px;
  }

  @media (min-width: 2560px) {
    width: 540px;
    height: 704px;
  }

  background: linear-gradient(
    27.55deg,
    rgba(255, 255, 255, 0.6) 0%,
    rgba(239, 239, 239, 0.1) 100%,
    rgba(255, 255, 255, 0.1) 100%
  );
  border-radius: 25px;
  backdrop-filter: blur(50px);

  transition: all 500ms ease-in;

  ${props => {
    switch (props.state) {
      case 'exiting':
        return `
        opacity: 0;
        visibility: hidden;
        transform: translate3d(0, -30%, 0);
        `
      default:
        return `
        visibility: visible;
        opacity: 1;
      `
    }
  }};
`

const HeaderTextWrapper = styled.div`
  @media (min-width: 768px) {
    padding: ${78 * 0.7}px;
  }

  @media (min-width: 1024px) {
    padding: ${78 * 0.8}px;
  }

  @media (min-width: 2560px) {
    padding: 78px;
  }
`

const HeaderText = styled(Text)`
  @media (min-width: 768px) {
    font-size: ${3.2 * 0.7}rem;
  }

  @media (min-width: 1024px) {
    font-size: ${3.2 * 0.8}rem;
  }

  @media (min-width: 2560px) {
    font-size: 3.2rem;
  }
`

const ImageWrapper = styled.div<{ state: TransitionStatus }>`
  position: absolute;

  @media (min-width: 768px) {
    width: ${720 * 0.7}px;
    height: ${1024 * 0.7}px;
  }

  @media (min-width: 1024px) {
    width: ${720 * 0.8}px;
    height: ${1024 * 0.8}px;
  }

  @media (min-width: 2560px) {
    width: 720px;
    height: 1024px;
  }

  transition: all 500ms ease-in;

  ${props => {
    switch (props.state) {
      case 'exiting':
        return `
        opacity: 0;
        visibility: hidden;
        transform: translate3d(0, 30%, 0);
        `
      default:
        return `
        visibility: visible;
        opacity: 1;
      `
    }
  }};
`

const getXPosition = ({ x }: { x: number }) => {
  if (x === 0) {
    return 'calc(0%)'
  } else if (x === 100) {
    return 'calc(100%)'
  }
  return `calc(${x}%`
}

const getYPosition = ({ y }: { y: number }) => {
  if (y === 0) {
    return 'calc(0%)'
  } else if (y === 100) {
    return 'calc(100%)'
  }
  return `calc(${y}%)`
}

const Emoji = styled.div<{
  state: TransitionStatus
  index: number
  x: number
  y: number
}>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  left: ${getXPosition};
  top: ${getYPosition};

  transform: translateX(-50%);

  @media (min-width: 720px) {
    font-size: ${40 * 0.7}px;
    width: ${92 * 0.7}px;
    height: ${92 * 0.7}px;
  }

  @media (min-width: 1024px) {
    font-size: ${40 * 0.8}px;
    width: ${92 * 0.8}px;
    height: ${92 * 0.8}px;
  }

  @media (min-width: 1440px) {
    font-size: 40px;
    width: 92px;
    height: 92px;
  }

  border-radius: 50%;

  background-color: ${COLORS.white};

  ${props => {
    switch (props.state) {
      case 'entered':
        return css`
          opacity: 1;
          animation: ${flipInX} 1s ease;
        `
      case 'exiting':
        return 'opacity: 1;'
      default:
        return `
        opacity: 0;
        `
    }
  }};
`

const Image = styled.img`
  position: absolute;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

const BackgroundImage = styled.img`
  z-index: 1;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

type EmojiProps = {
  // x position in percent from 0-100
  x: number
  // y position in percent from 0-100
  y: number
  emoji: string
}

type PageConfigProps = {
  text: string
  secondText?: string
  src: string
  srcSet: string
  alt: string
  width: number
  height: number
  emojis: EmojiProps[]
}

const PAGE_CONFIG: Array<PageConfigProps> = [
  {
    text: 'unhappyFirst',
    secondText: 'unhappySecond',
    src: '/static/images/unhappy-person.png',
    srcSet:
      '/static/images/unhappy-person.png, /static/images/unhappy-person@2x.png 2x, /static/images/unhappy-person@3x.png 3x',
    alt: 'an unhappy-looking person',
    width: 720,
    height: 1024,
    emojis: [
      {
        x: 0,
        y: 70,
        emoji: '😤',
      },
      {
        x: 100,
        y: 15,
        emoji: '💯',
      },
    ],
  },
  {
    text: 'handFirst',
    secondText: 'handSecond',
    src: '/static/images/hand-with-phone.png',
    srcSet:
      '/static/images/hand-with-phone.png, /static/images/hand-with-phone@2x.png 2x, /static/images/hand-with-phone@3x.png 3x',
    alt: 'a hand holding a smartphone',
    width: 720,
    height: 1024,
    emojis: [
      {
        x: 0,
        y: 50,
        emoji: '😀',
      },
      {
        x: 100,
        y: 20,
        emoji: '👌',
      },
    ],
  },
  {
    text: 'rocketFirst',
    src: '/static/images/rocket.png',
    srcSet:
      '/static/images/rocket.png, /static/images/rocket@2x.png 2x, /static/images/rocket@3x.png 3x',
    alt: 'a flying rocket',
    width: 720,
    height: 1024,
    emojis: [
      {
        x: 0,
        y: 40,
        emoji: '🤝',
      },
      {
        x: 100,
        y: 20,
        emoji: '😎',
      },
    ],
  },
]

const useContentProducer = (
  contentArray: Array<PageConfigProps>,
  delay: number,
  animationTime: number,
): { content: PageConfigProps; animate: boolean } => {
  const [index, setIndex] = useState(0)
  const [animate, setAnimate] = useState(false)
  const [content, setContent] = useState(contentArray[0])

  useEffect(() => {
    const interval = setInterval(() => {
      setContent(contentArray[index])
      setAnimate(false)
    }, animationTime)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index])

  useEffect(() => {
    const interval = setInterval(() => {
      if (index === contentArray.length - 1) {
        setIndex(0)
        setAnimate(true)
      } else {
        setIndex(index + 1)
        setAnimate(true)
      }
    }, delay)
    return () => clearInterval(interval)
  }, [delay, index, contentArray.length])

  return { content, animate } as const
}

const LeftCoverPageAnimations: React.FC = (): JSX.Element => {
  const animationTime = 1000
  const { t } = useTranslation('auth')
  const { animate, content } = useContentProducer(
    PAGE_CONFIG,
    10000,
    animationTime,
  )

  return (
    <CSSTransition in={!animate} timeout={animationTime} on>
      {animationState => (
        <>
          <GlassMorphism state={animationState}>
            <HeaderTextWrapper>
              <HeaderText size="giant" color="white" weight="bold">
                {t(`firstPageConfig.${content.text}`)}
              </HeaderText>
              {content.secondText && (
                <HeaderText size="giant" color="white" weight="bold">
                  {t(`firstPageConfig.${content.secondText}`)}
                </HeaderText>
              )}
            </HeaderTextWrapper>
            {content.emojis.map(({ x, y, emoji }, index) => (
              <Emoji
                state={animationState}
                index={index}
                key={emoji}
                x={x}
                y={y}
              >
                {emoji}
              </Emoji>
            ))}
          </GlassMorphism>
          <ImageWrapper state={animationState}>
            <Image
              src={content.src}
              srcSet={content.srcSet}
              width={content.width}
              height={content.height}
              alt={content.alt}
            />
          </ImageWrapper>
        </>
      )}
    </CSSTransition>
  )
}

function LeftCoverPage() {
  return (
    <Background>
      <BackgroundImage
        src="/static/images/blurred-background.jpg"
        srcSet="/static/images/blurred-background.jpg, /static/images/blurred-background@2x.jpg 2x, /static/images/blurred-background@3x.jpg 3x"
        alt="abstract blurred background"
      />
      <ContentWrapper>
        <LeftCoverPageAnimations />
      </ContentWrapper>
    </Background>
  )
}

export default LeftCoverPage
